import { Breakpoint } from '@altibox/design-system-component-lib';
import React, { FunctionComponent } from 'react';

import { DataLayerPurchaseWrapper } from 'src/components/data-layer-wrapper/data-layer-purchase-wrapper';
import { InformationCard } from 'src/components/information-card';
import { i18n } from 'src/i18n';
import { selectSelectedServiceLocationOrThrow, selectUserDetailsOrThrow } from 'src/state/address/selectors';
import { selectOrderConfirmation, selectSelectedProductOrThrow } from 'src/state/order/selectors';
import { useAppSelector } from 'src/state/thunk-api';
import { DateUtils } from 'src/utils/date-utils';
import { I18nUtils } from 'src/utils/i18n-utils';
import { OrderInformationUtils } from 'src/utils/order-information-utils';

import {
  StyledBulletList,
  StyledBulletListElement,
  StyledDescription,
  StyledDescriptionProductName,
  StyledDetailsRow,
  StyledDetailsTitle,
  StyledOrderDetails,
  StyledProductName,
  StyledProductsContainer,
  StyledProductTitle,
  StyledText,
  StyledTitle,
} from './receipt-card.styled';

export const ReceiptCard: FunctionComponent = () => {
  const serviceLocation = useAppSelector(selectSelectedServiceLocationOrThrow);
  const user = useAppSelector(selectUserDetailsOrThrow);
  const orderConfirmation = useAppSelector(selectOrderConfirmation)!;
  const selectedProduct = useAppSelector(selectSelectedProductOrThrow);

  const renderOrderDetails = () => {
    const items = [
      {
        title: i18n.page.success.receiptCard.orderNumber,
        text: orderConfirmation.orderNumber,
        isValueSensitive: true,
      },
      {
        title: i18n.page.success.receiptCard.payee,
        text: OrderInformationUtils.getFullName(user),
        isValueSensitive: true,
      },
      {
        title: i18n.page.success.receiptCard.paymentMethod,
        text: i18n.page.success.receiptCard.paymentMethodChosen,
      },
      {
        title: i18n.page.success.receiptCard.date,
        text: DateUtils.getFormattedDate(orderConfirmation.requestedCompletionDate),
      },
      {
        title: i18n.page.success.receiptCard.address,
        text: OrderInformationUtils.getFullAddress(serviceLocation),
        isValueSensitive: true,
      },
    ];

    return items.map((item) => (
      <StyledDetailsRow key={item.title}>
        <StyledDetailsTitle component="p" variant="paragraph3" maxBreakpoint={Breakpoint.TABLET}>
          {item.title}
        </StyledDetailsTitle>
        <StyledText
          component="p"
          variant="paragraph3"
          maxBreakpoint={Breakpoint.TABLET}
          data-hj-suppress={item.isValueSensitive}
        >
          {item.text}
        </StyledText>
      </StyledDetailsRow>
    ));
  };

  return (
    <DataLayerPurchaseWrapper product={selectedProduct} orderNumber={orderConfirmation.orderNumber}>
      <InformationCard $showBordersForMobile={true}>
        <StyledTitle component="h2" variant="paragraph1" maxBreakpoint={Breakpoint.TABLET}>
          {i18n.page.success.receiptCard.title}
        </StyledTitle>
        <StyledDescription component="p" variant="paragraph3">
          {I18nUtils.richMessage(i18n.page.success.receiptCard.description, {
            productName: (
              <StyledDescriptionProductName component="span" variant="paragraph3">
                {selectedProduct.name}
              </StyledDescriptionProductName>
            ),
          })}
        </StyledDescription>
        <StyledOrderDetails>{renderOrderDetails()}</StyledOrderDetails>
        <StyledProductsContainer>
          <StyledProductTitle component="p" variant="paragraph3" maxBreakpoint={Breakpoint.TABLET}>
            {i18n.page.success.receiptCard.products}
          </StyledProductTitle>
          <StyledProductName component="p" variant="paragraph3" maxBreakpoint={Breakpoint.TABLET}>
            {selectedProduct.name}
          </StyledProductName>
          {selectedProduct.includedItems && (
            <StyledBulletList>
              {selectedProduct.includedItems.map((item, index) => (
                <StyledBulletListElement variant="paragraph3" key={index}>
                  {item}
                </StyledBulletListElement>
              ))}
            </StyledBulletList>
          )}
        </StyledProductsContainer>
      </InformationCard>
    </DataLayerPurchaseWrapper>
  );
};
